import { ReactNode, useEffect, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { Box, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  BoldedTextDisclaimer,
  Subtitle,
  StyledInputMargin,
  StyledInput,
  MultiInputContainer,
  StyledPasswordInput,
  BackButton,
} from './SignUp.styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PasswordStrengthLevel } from '@components/PasswordStrength/PasswordStrength.types';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { registerInput } from '@utils/reactHookForm';
import { SignUpFormData } from './SignUp.types';
import { signUpFormSchema } from '@validationSchemas/signUp';
import { useMediaQueries } from '@utils/mediaQueries/mediaQueries';
import { calculatePasswordStrength } from './SignUp.utils';
import {
  Banner,
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Flex,
  PasswordRequirements,
  PasswordStrength,
  Text,
} from '@components';
import { ResidenceCountry } from '@features/api/response/ResidenceCountry';
import { useCountriesQuery } from '@features/api/appDataSlice';
import { useAuth } from '@hooks/useAuth';
import { useLazyUserQuery } from '@features/api/userSlice';
import { SignUpInvitesOnly } from '@utils/environmentVariables/environmentVariables';
import { hyperLinks } from '@config/config';

export const SignUp = ({
  countries,
}: {
  countries?: ResidenceCountry[];
}): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { lg: isTablet } = useMediaQueries();

  const [triggerGetUser] = useLazyUserQuery();
  const { signUp } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
    setValue,
    watch,
    control,
  } = useFormContext<SignUpFormData>();

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');
  const email = watch('emailAddress');
  const agreeToMarketingCommunications = watch(
    'agreeToMarketingCommunications'
  );
  const inviteCode = watch('inviteCode');

  useEffect(() => {
    const inviteCode = getValues('inviteCode');
    if (inviteCode && inviteCode?.indexOf(' ') > -1) {
      setValue('inviteCode', getValues('inviteCode')?.trim());
      trigger('inviteCode');
    }
  }, [inviteCode, trigger, setValue, getValues]);

  useEffect(() => {
    if (password && password !== '') {
      trigger('password');
      if (confirmPassword !== '') {
        trigger('confirmPassword');
      }
    }
    const email = getValues('emailAddress');
    if (email && email?.indexOf(' ') > -1) {
      setValue('emailAddress', getValues('emailAddress')?.trim());
      trigger('emailAddress');
    }
  }, [password, confirmPassword, email, trigger, setValue, getValues]);

  const passwordsDifferent =
    errors.confirmPassword?.type === 'oneOf' && password;

  const handleContinue = () =>
    navigate('/email-verification', {
      state: {
        from: '/signup',
        hasAgreedToMarketingCommunications: agreeToMarketingCommunications,
      },
    });

  // Removed isValid check because it's buggy and is false even though the form data is valid.
  // This won't be a problem because this function won't be called if there are errors
  const onSubmit = async (data: SignUpFormData) => {
    try {
      await signUp(data);
      const user = await triggerGetUser().unwrap();
      if (user) handleContinue();
    } catch (e) {
      console.error(e);
    }
  };

  const countriesMenuItems = useMemo(() => {
    return (
      countries?.map((x) => ({
        name: x.name,
        value: x.alpha3,
        icon: x.flag,
      })) ?? []
    );
  }, [countries]);

  return (
    <Flex flex={0}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box>
          <Box>
            <Box mt={8}>
              <Text variant='h4' fontWeight='fontWeightBold'>
                <FormattedMessage id='signUp.createAccount' />
              </Text>
              <Subtitle variant='h5' fontWeight='fontWeightRegular'>
                <FormattedMessage id='signUp.pleaseEnterDetails' />
              </Subtitle>
            </Box>
            <Box mt={12}>
              <Dropdown
                label={intl.formatMessage({
                  id: 'signUp.form.countryOfResidence',
                })}
                inputProps={{ 'data-testid': 'signup-country-residence' }}
                leftIcon={LocationOnOutlinedIcon}
                menuItemData={countriesMenuItems}
                {...registerInput(register, 'residenceCountry')}
                {...getErrorProps(intl, errors.residenceCountry)}
              />
            </Box>
            <MultiInputContainer>
              <StyledInputMargin
                label={intl.formatMessage({
                  id: 'signUp.form.firstName',
                })}
                inputProps={{
                  sx: {
                    textTransform: 'capitalize',
                  },
                  'data-testid': 'signup-first-name',
                }}
                {...registerInput(register, 'firstName')}
                {...getErrorProps(intl, errors.firstName)}
              />
              <StyledInputMargin
                label={intl.formatMessage({
                  id: 'signUp.form.middleName',
                })}
                inputProps={{
                  sx: {
                    textTransform: 'capitalize',
                  },
                  'data-testid': 'signup-middle-name',
                }}
                {...registerInput(register, 'middleName')}
                {...getErrorProps(intl, errors.middleName)}
              />
              <StyledInput
                label={intl.formatMessage({
                  id: 'signUp.form.surname',
                })}
                inputProps={{
                  sx: {
                    textTransform: 'capitalize',
                  },
                  'data-testid': 'signup-surname',
                }}
                {...registerInput(register, 'surname')}
                {...getErrorProps(intl, errors.surname)}
              />
            </MultiInputContainer>
            <Flex mt={8}>
              <Controller
                control={control}
                name='birthDate'
                render={({ field }) => (
                  <DatePicker
                    label={intl.formatMessage({ id: 'signUp.form.birthDate' })}
                    error={errors.birthDate}
                    mobileActions={['accept']}
                    disableFuture
                    showToolbar={false}
                    inputFormat='DD/MM/YYYY'
                    {...field}
                  />
                )}
              />
            </Flex>
            <Flex flexDirection='column'>
              <StyledInput
                label={intl.formatMessage({
                  id: 'misc.emailAddress',
                })}
                autoComplete='email'
                type='email'
                inputProps={{
                  'data-testid': 'signup-email-address',
                }}
                {...registerInput(register, 'emailAddress')}
                {...getErrorProps(intl, errors.emailAddress)}
              />
            </Flex>
            {SignUpInvitesOnly ? (
              <Flex>
                <StyledInput
                  label={intl.formatMessage({
                    id: 'signUp.inviteCode',
                  })}
                  autoComplete='none'
                  inputProps={{
                    'data-testid': 'signup-invite-code',
                  }}
                  {...registerInput(register, 'inviteCode')}
                  {...getErrorProps(intl, errors.inviteCode)}
                />
              </Flex>
            ) : null}
            <MultiInputContainer>
              <StyledPasswordInput
                label={intl.formatMessage({ id: 'misc.password' })}
                autoComplete='new-password'
                inputProps={{
                  'data-testid': 'signup-password',
                }}
                {...registerInput(register, 'password')}
                error={
                  errors.password?.type === 'required'
                    ? getErrorProps(intl, errors.password).error
                    : undefined
                }
                helperText={
                  errors.password?.type === 'required'
                    ? getErrorProps(intl, errors.password).helperText
                    : undefined
                }
              />
              {isTablet && (
                <Flex flex={1} flexDirection='column'>
                  <PasswordStrength
                    currentLevel={calculatePasswordStrength(
                      errors.password,
                      password
                    )}
                  />
                  {errors.password && errors.password?.type !== 'required' && (
                    <PasswordRequirements
                      errors={errors.password.message?.split('.')}
                    />
                  )}
                </Flex>
              )}

              <StyledPasswordInput
                label={intl.formatMessage({ id: 'misc.confirmPassword' })}
                autoComplete='new-password'
                inputProps={{
                  'data-testid': 'signup-confirm-password',
                }}
                {...registerInput(register, 'confirmPassword')}
                error={
                  errors.confirmPassword?.type === 'required'
                    ? getErrorProps(intl, errors.confirmPassword).error
                    : undefined
                }
                helperText={
                  errors.confirmPassword?.type === 'required'
                    ? getErrorProps(intl, errors.confirmPassword).helperText
                    : undefined
                }
              />
              {isTablet && passwordsDifferent && (
                <PasswordStrength
                  currentLevel={PasswordStrengthLevel.None}
                  error={
                    passwordsDifferent
                      ? intl.formatMessage({
                          id: 'signUp.errors.passwordsNotMatching',
                        })
                      : undefined
                  }
                />
              )}
            </MultiInputContainer>
            {!isTablet && (
              <>
                <PasswordStrength
                  currentLevel={calculatePasswordStrength(
                    errors.password,
                    password
                  )}
                  error={
                    passwordsDifferent
                      ? intl.formatMessage({
                          id: 'signUp.errors.passwordsNotMatching',
                        })
                      : undefined
                  }
                />
                {errors.password && errors.password?.type === 'matches' && (
                  <Text
                    variant='h6'
                    fontWeight='fontWeightSemiBold'
                    marginTop={2}
                  >
                    {intl.formatMessage({
                      id: errors.password.message ?? '',
                    })}
                  </Text>
                )}
                {errors.password &&
                  errors.password?.type !== 'required' &&
                  errors.password?.type !== 'matches' && (
                    <PasswordRequirements
                      errors={errors.password.message?.split('.')}
                    />
                  )}
              </>
            )}
          </Box>
        </Box>
        <Flex flexDirection='column' marginTop={17} gap={4}>
          <Checkbox
            label={
              <FormattedMessage
                id='signUp.termsAndConditions'
                values={{
                  termsAndConditions: (chunks: ReactNode[]): ReactNode => (
                    <BoldedTextDisclaimer
                      href={hyperLinks.termsAndConditions}
                      target='_blank'
                    >
                      {chunks}
                    </BoldedTextDisclaimer>
                  ),
                  privacyPolicy: (chunks: ReactNode[]): ReactNode => (
                    <BoldedTextDisclaimer
                      href={hyperLinks.privacyPolicy}
                      target='_blank'
                    >
                      {chunks}
                    </BoldedTextDisclaimer>
                  ),
                }}
              />
            }
            inputProps={{
              'data-testid': 'signup-has-accepted-terms',
            }}
            error={!!errors.hasAcceptedTerms}
            {...registerInput(register, 'hasAcceptedTerms')}
          />
          <Checkbox
            label={
              <FormattedMessage
                id='signUp.qualifiedInvestor'
                values={{
                  qualifiedInvestor: (chunks: ReactNode[]): ReactNode => (
                    <BoldedTextDisclaimer
                      href={hyperLinks.qualifiedInvestor}
                      target='_blank'
                    >
                      {chunks}
                    </BoldedTextDisclaimer>
                  ),
                }}
              />
            }
            inputProps={{
              'data-testid': 'signup-qualified-investor',
            }}
            error={!!errors.isQualifiedInvestor}
            {...registerInput(register, 'isQualifiedInvestor')}
          />
          <Checkbox
            label={<FormattedMessage id='signUp.marketingCommunications' />}
            inputProps={{
              'data-testid': 'marketing-communications',
            }}
            error={!!errors.agreeToMarketingCommunications}
            {...registerInput(register, 'agreeToMarketingCommunications')}
          />
          <Flex mt={16}>
            <Button
              data-testid='continue-button'
              fullWidth
              variant='primary'
              size='large'
              type='submit'
              color='primary'
            >
              <FormattedMessage id='misc.continue' />
            </Button>
          </Flex>
        </Flex>
      </form>
    </Flex>
  );
};

export const SignUpPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [searchParams] = useSearchParams();
  const hideBackParam = searchParams.get('hideBack');
  const hideBack = hideBackParam !== null && hideBackParam !== 'false';

  const methods = useForm({
    resolver: yupResolver(signUpFormSchema),
    mode: 'onSubmit',
    shouldFocusError: false,
    defaultValues: {
      countryOfResidence: '',
      birthDate: null,
      hasAcceptedTerms: false,
      isQualifiedInvestor: false,
    },
  });

  const { data: countries } = useCountriesQuery();

  const handleBackClick = () => navigate('/login');

  return (
    <Flex
      flexDirection='column'
      sx={{
        maxWidth: 750,
        width: '100%',
        flex: 1,
        justifyContent: 'center',
        alignSelf: 'center',
      }}
    >
      <Flex alignSelf='flex-start'>
        {hideBack ? null : (
          <BackButton
            variant='tertiary'
            color='primary'
            data-testid='signup-back-button'
            onClick={handleBackClick}
            sx={{ mb: 10 }}
          >
            <FormattedMessage id='misc.back' />
          </BackButton>
        )}
      </Flex>
      {SignUpInvitesOnly && (
        <Banner color='primary'>
          <Text variant='bodyBig' fontWeight='fontWeightMedium'>
            <FormattedMessage
              id='signUp.inviteCodeTooltipMessage'
              values={{
                linkedIn: (chunks: ReactNode[]): ReactNode => (
                  <BoldedTextDisclaimer
                    href={hyperLinks.linkedIn}
                    target='_blank'
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: 'underline',
                    }}
                  >
                    {chunks}
                  </BoldedTextDisclaimer>
                ),
              }}
            />
          </Text>
        </Banner>
      )}
      <FormProvider {...methods}>
        <SignUp countries={countries} />
      </FormProvider>
    </Flex>
  );
};
